// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@rpsc/App/Components/Notify";
import { CustomAppState } from "@rpsc/App/Store/Reducers";
import { getSettingsCollection } from "@rpsc/App/Store/Selectors/SettingsSelectors";
import {
    fieldDefinitions as infoCardFieldDefinitions,
    OwnProps as InfoCardModel,
} from "@rpsc/App/Widgets/Basic/InfoCard";
import React, { FC, useEffect, useState } from "react";

const enum fields {
    showTopicsFilter = "showTopicsFilter",
    maxNumOfCardsPerRow = "maxNumOfCardsPerRow",
    infoCardList = "infoCardList",
}

interface infoCardListModal extends InfoCardModel {
    id: string;
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showTopicsFilter]: boolean;
        [fields.maxNumOfCardsPerRow]: string;
        [fields.infoCardList]: infoCardListModal[];
    };
}

export interface InfoCardListStyles {
    topicsFilterWrapper?: InjectableCss;
    topicFilterSelect?: SelectPresentationProps;
    resetFilterButton?: ButtonPresentationProps;
    wrapper?: InjectableCss;
    cardWrapper?: InjectableCss;
    notifyWrapper?: InjectableCss;
    noCardsNotify?: InjectableCss;
}

export const infoCardListStyles: InfoCardListStyles = {};
const styles = infoCardListStyles;

const cardStyles = `.wrapper {
}

.image {
    &, & > div {
        height: 216px;
    }
}

.content-wrapper {
}

.heading-text {
    & > h3 {
        font-size: 20px;
        line-height:28px;
    }
    
}

.subHeading-text {
}`;

export const InfoCardList: FC<OwnProps> = ({ fields: { showTopicsFilter, infoCardList, maxNumOfCardsPerRow } }) => {
    const [selectedTopic, setSelectedTopic] = useState("");
    const [infoCards, setInfoCards] = useState(infoCardList);
    const isEditing = useShellContext().isEditing;

    const { resourcesAndNewsTopics, isMobileScreen } = useAppSelector(state => {
        return {
            resourcesAndNewsTopics: getSettingsCollection(state).resourcesAndNewsTopics,
            isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
        };
    });

    useEffect(() => {
        if (isEditing) {
            setInfoCards(infoCardList);
        }
    }, [isEditing, infoCardList]);

    // Message to show when no cards are added or when filter results are 0 (mainly for Resources & News Page )
    const noCardsMessage = (
        <StyledWrapper {...styles.notifyWrapper}>
            <Notify {...styles.noCardsNotify} type="info" showClose={false}>
                {siteMessage("Custom_ResourcesAndNews_EmptyList")}
            </Notify>
        </StyledWrapper>
    );

    if (!infoCardList || infoCardList.length === 0) {
        return showTopicsFilter ? noCardsMessage : null;
    }

    const handleSelectChange = (event: React.FormEvent<HTMLSelectElement>) => {
        const { value } = event.currentTarget;

        setSelectedTopic(value);
        setInfoCards(
            value === ""
                ? infoCardList
                : infoCardList.filter(item => item.fields.topics.some(topicItem => value === topicItem.fields.topic)),
        );
    };

    const clearFilter = () => {
        setInfoCards(infoCardList);
        setSelectedTopic("");
    };

    const numOfCardPerRowTablet = Math.ceil(parseInt(maxNumOfCardsPerRow, 10) / 2);

    return (
        <>
            {showTopicsFilter && (
                <StyledWrapper {...styles.topicsFilterWrapper} data-test-selector="FILTER">
                    <Select
                        {...styles.topicFilterSelect}
                        label={translate("Filter Resource By")}
                        labelPosition={isMobileScreen ? "top" : "left"}
                        value={selectedTopic || ""}
                        onChange={handleSelectChange}
                    >
                        <option value="">{translate("All Topics")}</option>
                        {resourcesAndNewsTopics.map(topic => (
                            <option key={topic.name} value={topic.name}>
                                {topic.name}
                            </option>
                        ))}
                    </Select>
                    <Button {...styles.resetFilterButton} onClick={clearFilter} aria-label="Reset">
                        {translate("Reset")}
                    </Button>
                </StyledWrapper>
            )}
            {infoCards.length === 0 ? (
                noCardsMessage
            ) : (
                <StyledWrapper {...styles.wrapper} {...{ maxNumOfCardsPerRow, numOfCardPerRowTablet }}>
                    {infoCards.map((item, index) => {
                        const fields = item.fields;
                        return (
                            <StyledWrapper key={index} {...styles.cardWrapper}>
                                {createWidgetElement("Basic/InfoCard", {
                                    fields: { ...fields, customCSS: cardStyles },
                                })}
                            </StyledWrapper>
                        );
                    })}
                </StyledWrapper>
            )}
        </>
    );
};

const infoCardList: WidgetModule = {
    component: InfoCardList,
    definition: {
        group: "Basic",
        icon: "image-landscape",
        fieldDefinitions: [
            {
                name: fields.showTopicsFilter,
                fieldType: "Contextual",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tooltip: "Filter for Resources & News Page",
            },
            {
                name: fields.maxNumOfCardsPerRow,
                fieldType: "Contextual",
                displayName: "Maximum cards per row",
                editorTemplate: "DropDownField",
                defaultValue: "3",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                ],
            },
            {
                name: fields.infoCardList,
                fieldType: "General",
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: (item: HasFields) => {
                    return item.fields.id;
                },
                fieldDefinitions: [
                    {
                        name: "id",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        fieldType: "General",
                    },
                    ...infoCardFieldDefinitions,
                ] as any,
            },
        ],
    },
};

export default infoCardList;
